import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ContentChildren,
  Directive,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  QueryList,
  Renderer2,
} from '@angular/core';
import { TableRowDirective } from './table-row.directive';
import { getKeyCode, KEY_CODES } from '@backbase/ui-ang/util';
import { auditTime, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
@Directive({
  selector: '[bbTableFocus]',
  standalone: false,
})
/**
 * Enables keyboard navigation and focus management for table rows.
 *
 * @remarks
 * This directive provides keyboard accessibility for tables by handling arrow key navigation
 * (UP/DOWN) and Enter key interactions on table rows. It automatically manages focus state
 * and row selection through keyboard input. Works in conjunction with {@link TableRowDirective}
 * to track and manage individual row elements within the table.
 *
 * The directive preserves native table semantics. It does not apply ARIA grid roles because
 * focus moves between rows rather than between cells in a two-dimensional grid.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/grid_role
 *
 * The directive monitors changes to the row list and can optionally focus the first row
 * when new rows are added or the list is updated.
 *
 * @example
 *
 * ```html
 * <table [bbTable]="data" bbTableFocus>
 *   <tbody>
 *     <tr [bbRow]="rowData" *ngFor="let rowData of data">
 *       <td>{{ rowData.name }}</td>
 *     </tr>
 *   </tbody>
 * </table>
 * ```
 */
export class TableFocusDirective implements AfterViewInit, OnDestroy {
  private focusedItemIndex = -1;

  /**
   * Virtual scroll viewport instance.
   *
   * @remarks
   * Used to track the scroll position and manage the tab stop when using a virtual scroll viewport.
   */
  private readonly viewport = inject(CdkVirtualScrollViewport, { optional: true });

  /**
   * Document instance.
   *
   * @remarks
   * Used to access the document object and manage the tab stop when using a virtual scroll viewport.
   */
  private readonly document = inject(DOCUMENT);

  /**
   * Query list of all table row elements managed by this directive.
   *
   * @remarks
   * Contains references to all {@link TableRowDirective} instances that are children of the table.
   * Used internally to track focusable row elements and manage keyboard navigation between rows.
   */
  @ContentChildren(TableRowDirective, { read: ElementRef, descendants: true, emitDistinctChangesOnly: false })
  listItems!: QueryList<ElementRef>;
  /**
   * Flag indicating whether to focus the first row when the row list changes.
   *
   * @remarks
   * When set to `true`, the directive will automatically focus the first row element whenever
   * the list of rows is updated (e.g., after pagination, sorting, or filtering). Useful for
   * maintaining focus state during dynamic table updates without losing keyboard navigation context.
   *
   * @example
   *
   * ```html
   * <table [bbTable]="data" bbTableFocus [focusFirstRowOnChanges]="true">
   *   <tbody>
   *     @for (row of data; track row.id) {
   *       <tr [bbRow]="row">
   *         <td>{{ row.name }}</td>
   *       </tr>
   *     }
   *   </tbody>
   * </table>
   * ```
   */
  @Input() focusFirstRowOnChanges = false;
  private destroy$ = new Subject<void>();
  constructor(private readonly renderer: Renderer2) {}
  /**
   * Handles keyboard events on the table for navigation and row activation.
   *
   * @remarks
   * Processes keyboard input for table navigation:
   * - **DOWN arrow**: Moves focus to the next row (if available)
   * - **UP arrow**: Moves focus to the previous row (if available)
   * - **ENTER key**: Activates the currently focused row by simulating a click event
   *
   * The directive prevents default browser behavior for these keys to ensure consistent
   * table navigation experience. Only activates clicks on elements with the 'active' class
   * to avoid triggering clicks on headers.
   *
   * @example
   *
   * ```html
   * <table [bbTable]="countries" bbTableFocus (rowClick)="onRowClick($event)">
   *   <tbody>
   *     @for (country of countries; track country.id) {
   *       <tr [bbRow]="country">
   *         <td>{{ country.name }}</td>
   *       </tr>
   *     }
   *   </tbody>
   * </table>
   * ```
   */
  @HostListener('keydown', ['$event'])
  onKeyEvent(event: KeyboardEvent) {
    switch (getKeyCode(event)) {
      case KEY_CODES.DOWN:
        this.moveFocus(event, 1);
        break;
      case KEY_CODES.UP:
        this.moveFocus(event, -1);
        break;
      case KEY_CODES.ENTER:
        const currentActiveItem = this.listItems.find(
          (item) =>
            // ignore the header and to only "click" on data in the table
            item.nativeElement.tagName === (event.target as HTMLElement)?.tagName &&
            item.nativeElement.classList.contains('active'),
        );
        if (currentActiveItem) {
          currentActiveItem.nativeElement.click();
          event.preventDefault();
        }
        break;
    }
  }
  private moveFocus(event: KeyboardEvent, step: 1 | -1) {
    if (this.isInsideHeader(event.target)) {
      return;
    }

    const currentIndex = this.indexOfRowContaining(event.target);
    const fromIndex = currentIndex === -1 ? this.focusedItemIndex : currentIndex;
    this.focusedItemIndex = Math.min(Math.max(fromIndex + step, 0), this.listItems.length - 1);
    this.focusItem();
    event.preventDefault();
  }
  private indexOfRowContaining(target: EventTarget | null): number {
    const row = target instanceof Element ? target.closest('tr') : null;

    return row ? this.listItems.toArray().findIndex((item) => item.nativeElement === row) : -1;
  }
  private isInsideHeader(target: EventTarget | null): boolean {
    return target instanceof Element && target.closest('thead, th') !== null;
  }

  private syncTabStopToVisibleRow() {
    const activeElement = this.document.activeElement;
    const viewportElement = this.viewport?.getElementRef().nativeElement;

    // Focus on a row or on the viewport itself (e.g. during keyboard navigation) must not be displaced.
    if (this.indexOfRowContaining(activeElement) !== -1 || viewportElement?.contains(activeElement)) {
      return;
    }
    this.focusedItemIndex = this.indexOfFirstVisibleRow();
    this.focusItem(false);
  }
  private indexOfFirstVisibleRow(): number {
    if (!this.viewport) {
      return 0;
    }
    const viewportTop = this.viewport.getElementRef().nativeElement.getBoundingClientRect().top;
    const index = this.listItems
      .toArray()
      .findIndex((item) => item.nativeElement.getBoundingClientRect().bottom > viewportTop);

    return Math.max(index, 0);
  }
  /**
   * Applies focus to the row at the current focused index.
   *
   * @remarks
   * Updates all row elements' `tabIndex` attribute to ensure they are keyboard-accessible,
   * then focuses the specific row corresponding to the current focused item index.
   * This method is called after keyboard navigation to move focus between rows.
   *
   * @param focusNativeElement - If `true`, the DOM element at the focused index will receive keyboard focus.
   *                             If `false`, only the tabIndex attribute is updated without focusing the element.
   */
  private focusItem(focusNativeElement = true) {
    this.listItems.forEach((el, index) => {
      if (index === this.focusedItemIndex) {
        this.renderer.setAttribute(el.nativeElement, 'tabIndex', '0');
        if (focusNativeElement) {
          el.nativeElement.focus();
        }
      } else {
        this.renderer.setAttribute(el.nativeElement, 'tabIndex', '-1');
      }
    });
  }
  /**
   * Makes first row in a table focusable
   *
   * @remarks
   * This method sets up the subscription to monitor changes in the table row list.
   * When the list of rows changes (e.g., due to data updates, pagination, or filtering),
   * a row that still has focus stays the tab stop. Otherwise the tab stop resets to the first row
   * and that row is optionally focused based on the {@link focusFirstRowOnChanges} flag.
   *
   * Inside a `cdk-virtual-scroll-viewport`, the tab stop resets to the first visible row instead of
   * the first rendered row, and follows the first visible row while scrolling when no row has focus.
   *
   * The subscriptions use the {@link destroy$} subject to automatically unsubscribe when the
   * directive is destroyed, preventing memory leaks.
   *
   * @internal
   */
  ngAfterViewInit(): void {
    if (this.listItems.first?.nativeElement) {
      this.focusedItemIndex = 0;
      // false: only set tabIndex without stealing focus on initial render
      this.focusItem(false);
    }
    this.listItems.changes.pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => {
        const activeIndex = this.indexOfRowContaining(this.document.activeElement);
        if (activeIndex !== -1) {
          this.focusedItemIndex = activeIndex;
          this.focusItem(false);
        } else if (items.first?.nativeElement) {
          this.focusedItemIndex = this.indexOfFirstVisibleRow();
          this.focusItem(this.focusFirstRowOnChanges);
        }
      },
    });
    this.viewport
      ?.elementScrolled()
      .pipe(auditTime(100), takeUntil(this.destroy$))
      .subscribe(() => this.syncTabStopToVisibleRow());
  }
  /**
   * Cleans up subscriptions to row list changes.
   *
   * @remarks
   * This method handles cleanup of the directive's reactive subscriptions and resources.
   * It completes the {@link destroy$} subject, which automatically unsubscribes all observables
   * that are piped with `takeUntil(this.destroy$)`. This ensures proper resource cleanup and
   * prevents memory leaks when the directive is removed from the DOM.
   *
   * @remarks
   * The destroy$ subject acts as a RxJS unsubscribe trigger for all
   * active subscriptions within the directive, particularly the row list change subscription
   * set up in {@link ngAfterViewInit}.
   *
   * @internal
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
